import type { DocumentRef } from '../../lib/documentReader'
import { formatNumberString, formatOfficerDate, humanizeToken } from '../../lib/officerFormat'
import type { BookSourceAssertion } from './bookReview'

export const MATCH_FIELDS = [
  ['transaction_type', 'Description', 'enum'],
  ['units_delta', 'Capital movement', 'number'],
  ['amount', 'Cash consideration', 'number'],
  ['currency', 'Cash currency', 'text'],
  ['effective_date', 'Financial date', 'date'],
  ['settlement_date', 'Settlement date', 'date'],
  ['posting_date', 'Bank posting date', 'date'],
  ['consideration_description', 'Consideration description', 'text'],
] as const
export type MatchingKey = (typeof MATCH_FIELDS)[number][0]
export type MatchingValues = Partial<Record<MatchingKey, string | null>>
export interface MatchingCandidate {
  trade_id: string
  values: MatchingValues
  manual_entry: boolean
  documents: readonly DocumentRef[]
}
export interface CapitalMatchingReview {
  kind: 'capital_matching'
  group_id: string
  proposal_id: string
  document_id: string
  decision_url: string
  reviewed_binding: string
  holder: { id: string; name: string }
  instrument: { id: string; name: string }
  issuer: { id: string; name: string }
  proposed: MatchingValues
  candidates: readonly MatchingCandidate[]
  source_assertions: Partial<Record<MatchingKey, readonly BookSourceAssertion[]>>
  documents: readonly DocumentRef[]
  dependencies: readonly {
    proposal_id: string
    status: string
    receipt_ids: readonly string[]
    ready: boolean
  }[]
  can_submit: boolean
}
export interface CapitalMatchCommand {
  outcome: 'same_trade' | 'new_trade'
  reviewed_binding: string
  idempotency_key: string
  trade_id: string | null
}
export interface CapitalMatchReceipt {
  proposal_id: string
  outcome: 'same_trade' | 'new_trade'
  trade_id: string
  record_event_id: string
  canonical_record_event_id: string
  recorded_values: MatchingValues | null
  group_status: string
}

export function matchingUrl(review: CapitalMatchingReview): string {
  const expected = `/api/documents/${encodeURIComponent(review.document_id)}/commitment-proposals/${encodeURIComponent(review.proposal_id)}/matching`
  if (review.decision_url !== expected)
    throw new Error('This matching review has an invalid decision path.')
  return review.decision_url
}
export function matchingCommand(
  review: CapitalMatchingReview,
  choice: string,
  key: string
): CapitalMatchCommand {
  matchingUrl(review)
  if (!review.can_submit || review.dependencies.some((item) => !item.ready))
    throw new Error('Book the required earlier records first.')
  if (choice !== 'new' && !review.candidates.some((item) => item.trade_id === choice))
    throw new Error('Choose the recorded movement or a separate movement.')
  return {
    outcome: choice === 'new' ? 'new_trade' : 'same_trade',
    reviewed_binding: review.reviewed_binding,
    idempotency_key: key,
    trade_id: choice === 'new' ? null : choice,
  }
}
export function matchingReceiptMatches(
  review: CapitalMatchingReview,
  command: CapitalMatchCommand,
  receipt: CapitalMatchReceipt
): boolean {
  return (
    receipt.proposal_id === review.proposal_id &&
    receipt.outcome === command.outcome &&
    !!receipt.record_event_id &&
    !!receipt.canonical_record_event_id &&
    !!receipt.trade_id &&
    (command.outcome === 'same_trade'
      ? receipt.trade_id === command.trade_id
      : !review.candidates.some((item) => item.trade_id === receipt.trade_id))
  )
}
export function matchingText(value: unknown, format: (typeof MATCH_FIELDS)[number][2]): string {
  if (value === null || value === undefined || value === '') return 'Not stated'
  const text = String(value)
  if (format === 'number') return formatNumberString(text)
  if (format === 'date') return formatOfficerDate(text) ?? text
  if (format === 'enum') return humanizeToken(text)
  return text
}
